import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Divider, Input } from '@nextui-org/react';
import { Eye, EyeOff, LogIn, Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface LocationState {
  from?: { pathname?: string };
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isAuthenticated) {
      const state = location.state as LocationState | null;
      const target = state?.from?.pathname || '/';
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, location.state, navigate]);

  const validate = (): string | null => {
    if (!email.trim()) return 'Bitte gib deine E-Mail-Adresse ein.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return 'Bitte gib eine gültige E-Mail-Adresse ein.';
    if (!password) return 'Bitte gib dein Passwort ein.';
    if (password.length < 6) return 'Das Passwort muss mindestens 6 Zeichen lang sein.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await login({ email: email.trim().toLowerCase(), password });
      const state = location.state as LocationState | null;
      navigate(state?.from?.pathname || '/', { replace: true });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (err?.response?.status === 401
          ? 'E-Mail oder Passwort ist falsch.'
          : 'Anmeldung fehlgeschlagen. Bitte versuche es später erneut.');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Einloggen | DartCloud</title>
        <meta name="description" content="Logge dich in dein DartCloud-Konto ein, um Turniere zu verwalten, Statistiken zu sehen und mit der Community zu interagieren." />
      </Helmet>

      <div className="min-h-[calc(100vh-6rem)] flex items-center justify-center px-4 py-12 bg-black">
        <Card className="w-full max-w-md bg-zinc-900/80 border border-zinc-800 backdrop-blur">
          <CardHeader className="flex flex-col items-start gap-2 px-6 pt-6 pb-2">
            <div className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#3fced1]/10 text-[#3fced1]">
                <LogIn size={20} />
              </span>
              <h1 className="text-2xl font-bold text-white">Willkommen zurück</h1>
            </div>
            <p className="text-sm text-gray-400">
              Melde dich mit deiner E-Mail-Adresse und deinem Passwort an.
            </p>
          </CardHeader>

          <CardBody className="px-6 pb-6 pt-2">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
              <Input
                type="email"
                label="E-Mail"
                placeholder="deine@email.de"
                value={email}
                onValueChange={setEmail}
                isRequired
                autoComplete="email"
                startContent={<Mail size={18} className="text-gray-400" />}
                variant="bordered"
                classNames={{
                  inputWrapper:
                    'bg-zinc-950 border-zinc-700 data-[hover=true]:border-zinc-600 group-data-[focus=true]:border-[#3fced1]',
                  input: 'text-white placeholder:text-gray-500',
                  label: 'text-gray-300',
                }}
              />

              <Input
                type={showPassword ? 'text' : 'password'}
                label="Passwort"
                placeholder="Mindestens 6 Zeichen"
                value={password}
                onValueChange={setPassword}
                isRequired
                autoComplete="current-password"
                startContent={<Lock size={18} className="text-gray-400" />}
                endContent={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                }
                variant="bordered"
                classNames={{
                  inputWrapper:
                    'bg-zinc-950 border-zinc-700 data-[hover=true]:border-zinc-600 group-data-[focus=true]:border-[#3fced1]',
                  input: 'text-white placeholder:text-gray-500',
                  label: 'text-gray-300',
                }}
              />

              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-end -mt-1">
                <RouterLink
                  to="/passwort-vergessen"
                  className="text-sm text-[#3fced1] hover:text-[#35b5b8] transition-colors"
                >
                  Passwort vergessen?
                </RouterLink>
              </div>

              <Button
                type="submit"
                isLoading={isSubmitting}
                className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                size="lg"
              >
                {isSubmitting ? 'Anmeldung läuft …' : 'Einloggen'}
              </Button>
            </form>

            <Divider className="my-6 bg-zinc-800" />

            <p className="text-center text-sm text-gray-400">
              Noch kein Konto?{' '}
              <RouterLink
                to="/register"
                className="text-[#3fced1] hover:text-[#35b5b8] font-medium transition-colors"
              >
                Jetzt registrieren
              </RouterLink>
            </p>
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default LoginPage;
